// Password-based auth + role cache. Replaces assets/js/admin/auth.js.
// Exposes the same `auth.*` surface that legacy view modules used, so the
// IIFE views keep working unchanged.

import type { Role, UserProfile } from './db/types';
import type { ProfileFetchResult, SupaWrapper } from './supabase-client';

export type ProfileState =
  | { kind: 'ok' }
  | { kind: 'profile_missing' }
  | { kind: 'invalid_role'; raw: string }
  | { kind: 'error'; message: string };

const SID_KEY = 'ifi_session_id';
const ROLE_KEY = 'ifi_studio_role';

export interface AuthApi {
  readonly mode: 'supabase';
  isAuthed(): boolean;
  isAuthedAsync(): Promise<boolean>;
  signIn(email: string, password: string): Promise<void>;
  signUp(email: string, password: string, fullName?: string): Promise<{ needsConfirm: boolean }>;
  resetPassword(email: string): Promise<void>;
  updatePassword(newPassword: string): Promise<void>;
  logout(): Promise<void>;
  getRole(): Role;
  getProfile(): UserProfile | null;
  getProfileState(): ProfileState | null;
  fetchProfile(): Promise<UserProfile | null>;
  getSessionId(): string;
  onAuthChange(cb: (evt: string) => void): () => void;
}

function stateFromResult(r: ProfileFetchResult): ProfileState {
  if (r.kind === 'ok') return { kind: 'ok' };
  if (r.kind === 'profile_missing') return { kind: 'profile_missing' };
  if (r.kind === 'invalid_role') return { kind: 'invalid_role', raw: r.raw };
  return { kind: 'error', message: r.message };
}

function getSessionId(): string {
  let sid = sessionStorage.getItem(SID_KEY);
  if (!sid) {
    sid = 'SES-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
    sessionStorage.setItem(SID_KEY, sid);
  }
  return sid;
}

export function createAuth(supa: SupaWrapper): AuthApi {
  let cached: UserProfile | null = null;
  let lastState: ProfileState | null = null;

  function isAuthedSync(): boolean {
    try {
      return localStorage.getItem('ifi_authed') === '1';
    } catch (_) {
      return false;
    }
  }

  return {
    mode: 'supabase',

    isAuthed() {
      return isAuthedSync();
    },
    async isAuthedAsync() {
      const s = await supa.getSession();
      return !!s.user;
    },

    async signIn(email, password) {
      await supa.signIn(email, password);
      const r = await supa.fetchUserProfileDetailed();
      lastState = stateFromResult(r);
      cached = r.kind === 'ok' ? r.profile : null;
      if (cached) sessionStorage.setItem(ROLE_KEY, cached.role);
      else sessionStorage.removeItem(ROLE_KEY);
    },

    async signUp(email, password, fullName) {
      return supa.signUp(email, password, fullName);
    },

    async resetPassword(email) {
      return supa.resetPassword(email);
    },

    async updatePassword(newPassword) {
      return supa.updatePassword(newPassword);
    },

    async logout() {
      cached = null;
      lastState = null;
      sessionStorage.removeItem(ROLE_KEY);
      await supa.signOut();
    },

    getRole(): Role {
      if (cached?.role) return cached.role;
      const stored = sessionStorage.getItem(ROLE_KEY) as Role | null;
      return stored ?? 'viewer';
    },

    getProfile() {
      return cached;
    },

    getProfileState() {
      return lastState;
    },

    async fetchProfile() {
      const r = await supa.fetchUserProfileDetailed();
      lastState = stateFromResult(r);
      cached = r.kind === 'ok' ? r.profile : null;
      if (cached) sessionStorage.setItem(ROLE_KEY, cached.role);
      else sessionStorage.removeItem(ROLE_KEY);
      return cached;
    },

    getSessionId,

    onAuthChange(cb) {
      if (!supa.client) return () => {};
      const { data } = supa.client.auth.onAuthStateChange((evt: string) => {
        if (evt === 'SIGNED_OUT') {
          cached = null;
          lastState = null;
        }
        cb(evt);
      });
      return () => data?.subscription?.unsubscribe?.();
    },
  };
}
